import React, { ReactNode, useEffect, useRef } from 'react';
import { useSocketListener } from '@/hooks/use.socket.listener';
import { Conference, ConferenceTypeEnum, EventTypeEnum, Packet } from '@matap/shared';
import { actionClearCall, actionSetCallConnection } from '@/redux';
import { actionShowAlert } from '@/redux/general.store';
import { useCallConnection } from '@/hooks/use.call.connection';
import { useAppDispatch } from '@/hooks/use.redux';
import { usePermissions } from '@/hooks/use.permissions';
import { useSocket } from '@/hooks/use.socket';
import { RemoteView } from '@/components/compose/call/rtc-view/remote.view';
import { ModalNamesEnum } from '@/constants/enums/modal.names.enum';
import { SDP } from '@/webrtc/sdp';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';

interface ProviderProps {
  children: ReactNode;
}
function BaseConferenceProvider({ children }: ProviderProps) {
  const { activeCallId, activeCall, isPip } = useCallConnection();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<NativeStackNavigationProp<any>>();
  const { hangupCall, createConferenceRoom } = useSocket();
  const { fetchPermissions, mic, camera } = usePermissions();
  const incomingCallRef = useRef<Conference>(null);

  useSocketListener(
    EventTypeEnum.CALL_REQUEST,
    async (packet: Packet<Conference>) => {
      if (activeCallId) {
        hangupCall(packet.payload._id, 'کاربر طرف مقابل شما اشغال است');
        return;
      }
      incomingCallRef.current = packet.payload;

      const callType = packet.payload.type === ConferenceTypeEnum.audio ? 'audio' : 'video';
      const hasAccess = await fetchPermissions(callType === 'video' ? 'camera_mic' : 'mic');

      if (!hasAccess) {
        hangupCall(packet.payload._id, 'کاربر مقابل شما اجازه های لازم برای برقراری این تماس را نداده است');
        return;
      }

      if (incomingCallRef.current) {
        dispatch(actionSetCallConnection(createConferenceRoom(incomingCallRef.current)));
      }
    },
    [activeCallId, hangupCall, fetchPermissions, camera, mic],
  );

  useSocketListener(
    EventTypeEnum.CALL_ACCEPTED,
    (packet: Packet<{ _id: string }>) => {
      if (activeCallId && activeCallId === packet.payload._id && !activeCall.isStarted()) {
        activeCall.start();
      }
    },
    [activeCallId, activeCall],
  );

  useSocketListener(
    EventTypeEnum.CALL_ENDED,
    (packet: Packet<{ _id: string; reason: string }>) => {
      if (incomingCallRef.current?._id === packet.payload._id) {
        incomingCallRef.current = null;
      }
      if (activeCallId && activeCallId === packet.payload._id) {
        dispatch(actionClearCall());
        activeCall.destroy();
        if (packet.payload.reason) {
          dispatch(actionShowAlert(packet.payload.reason));
        }
      }
    },
    [activeCallId, activeCall],
  );

  useSocketListener(
    EventTypeEnum.EXCHANGE_SDP,
    (packet: Packet<SDP>) => {
      if (activeCallId && activeCallId === packet.payload.conferenceId) {
        activeCall.onSDP(packet.payload);
      }
    },
    [activeCallId, activeCall],
  );
  /* */
  useEffect(() => {
    if (activeCallId && !isPip) {
      navigation.navigate(ModalNamesEnum.Call as any);
    }
  }, [activeCallId, isPip, navigation]);

  return (
    <>
      {activeCall && isPip && activeCall.isStarted() && <RemoteView pip connection={activeCall} />}
      {children}
    </>
  );
}

export const ConferenceProvider = React.memo(BaseConferenceProvider);
